import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, img, load } from "@/lib/api";

const StoreCtx = createContext(null);
export const useStore = () => useContext(StoreCtx);

const mergeItem = (cart, item) => {
  const i = cart.findIndex((c) => c.product_id === item.product_id && c.variant === item.variant);
  if (i === -1) return [...cart, item];
  return cart.map((c, j) => (j === i ? { ...c, quantity: Math.min(10, c.quantity + item.quantity) } : c));
};

const setMeta = (site) => {
  const r = document.documentElement.style;
  r.setProperty("--rh-primary", site.theme.primary);
  r.setProperty("--rh-accent", site.theme.accent);
  document.title = site.seo.title;
  document.querySelector('meta[name="description"]')?.setAttribute("content", site.seo.description);
  if (site.brand.favicon_url) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.removeAttribute("type");
    link.href = img(site.brand.favicon_url);
  }
};

export function StoreProvider({ children }) {
  const [site, setSite] = useState(null);
  const [products, setProducts] = useState([]);
  const [failed, setFailed] = useState(false);
  const [cart, setCart] = useState(() => load("rh_cart", []));
  const [wish, setWish] = useState(() => load("rh_wish", []));
  const [bagOpen, setBagOpen] = useState(false);
  const [added, setAdded] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get("/site"), api.get("/products")]);
      setSite(s.data);
      setProducts(p.data);
      setMeta(s.data);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => localStorage.setItem("rh_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("rh_wish", JSON.stringify(wish)), [wish]);

  useEffect(() => {
    if (!cart.length) {
      setQuote(null);
      setQuoteError("");
      return;
    }
    let live = true;
    api.post("/cart/quote", { items: cart })
      .then((r) => live && (setQuote(r.data), setQuoteError("")))
      .catch((e) => live && (setQuote(null), setQuoteError(errMsg(e))));
    return () => { live = false; };
  }, [cart]);

  const addToBag = useCallback(async (product, variant = "", quantity = 1, { silent } = {}) => {
    const next = mergeItem(cart, { product_id: product.id, variant, quantity });
    try {
      await api.post("/cart/quote", { items: next });
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
    setCart(next);
    if (!silent) setAdded({ product, variant, quantity, at: Date.now() });
  }, [cart]);

  const updateQty = (i, q) => setCart((c) => c.map((x, j) => (j === i ? { ...x, quantity: Math.max(1, Math.min(10, q)) } : x)));
  const removeItem = (i) => setCart((c) => c.filter((_, j) => j !== i));
  const clearCart = () => setCart([]);
  const toggleWish = (id) => {
    const on = !wish.includes(id);
    setWish((w) => (on ? [...w, id] : w.filter((x) => x !== id)));
    toast(on ? "Saved to your wishlist" : "Removed from your wishlist");
  };

  const value = useMemo(() => ({
    site, products, failed, refresh, cart, quote, quoteError, addToBag, updateQty, removeItem, clearCart,
    wish, toggleWish, bagOpen, setBagOpen, added, setAdded,
    count: cart.reduce((s, c) => s + c.quantity, 0),
  }), [site, products, failed, refresh, cart, quote, quoteError, addToBag, wish, bagOpen, added]); // eslint-disable-line

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}
